<template>
  <div
    class="input--border input--radius bg__color--white"
    :class="{ 'input--disabled': disabled }"
  >
    <div :class="{ 'input__area': showCurrency }">
      <label
        :for="id"
        class="cursor--pointer"
        :aria-label="t(`currencies.${currentCurrency.toLowerCase()}_label`)"
        v-if="showCurrency"
      >
        {{ getSymbol(currentCurrency) }}
      </label>

      <input-cleave
        type="text"
        class="input"
        :id="id"
        :placeholder="placeholder"
        :name="name"
        :disabled="disabled"
        :options="options"
        :format="formatAmount"
        :unformat="unformatAmount"
        v-model.number="amount"
        @blur="handleInputBlurred()"
      />
    </div>

    <p
      class="input--errors input--error"
      v-if="v?.$error"
    >
      <template v-if="v.numeric?.$invalid">
        {{ t('errors.input_number_not_numeric') }}
      </template>

      <template v-else-if="v.required?.$invalid">
        {{ t('errors.input_number_required') }}
      </template>

      <template v-else-if="v.minAmount?.$invalid">
        {{ t('errors.input_number_min_value_currency', { value: getFormatedAmount({ amount: v.minAmount.$params.min, currency: currentCurrency }) }) }}
      </template>
    </p>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useCurrencyStore } from '@/stores/CurrencyStore'
import { useI18n } from '@/vendors/i18n'
import { useMoney } from '@/composables/app/useMoney'
import { formatNumeral, unformatNumeral } from 'cleave-zen'
import { guid } from '@/utils/utils'

import InputCleave from '&/atoms/InputCleave'

const { t } = useI18n()
const { getSymbol, getThousandsSeparator, getDecimalsSeparator } = useCurrencyStore()
const { currency: defaultCurrency } = storeToRefs(useCurrencyStore())
const { getFormatedAmount } = useMoney()

const model = defineModel({ type: [Number, String], default: null })

const { currency, v } = defineProps({
  id: { type: String, default: () => Math.random().toString() },
  placeholder: { type: String, default: '...' },
  disabled: { type: Boolean, default: false },
  currency: { type: String },
  showCurrency: { type: Boolean, default: true },
  v: Object
})

const name = `input_amount${guid(1)}`

const currentCurrency = computed(() => currency || defaultCurrency.value)
const thousandsSeparator = computed(() => getThousandsSeparator(currentCurrency.value))
const decimalsSeparator = computed(() => getDecimalsSeparator(currentCurrency.value))

const options = computed(() => ({
  delimiter: thousandsSeparator.value,
  numeralDecimalMark: decimalsSeparator.value,
  numeralDecimalScale: 2,
  numeralPositiveOnly: true,
  numeral: true
}))

const amount = computed({
  get: () => model.value ? (model.value / 100).toString().replace('.', decimalsSeparator.value) : 0,
  set: value => model.value = Math.round(value.toFixed(2) * 100)
})

const formatAmount = value => value ? formatNumeral(value, options.value) : ''
const unformatAmount = value => value ? unformatNumeral(value, options.value) : 0

const handleInputBlurred = () => v?.$touch()
</script>

<style lang="scss" scoped>
.input {
  background: transparent;

  &__area {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding-left: 12px;
    white-space: nowrap;
  }
}
</style>
